import { Component } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import FormModal from "components/DigiHapi/FormModal";

// Large numbered step icon: the MD3 theme icon (13px dot) makes the current step hard to spot
function WizardStepIcon({ active, completed, icon }) {
	return (
		<MDBox
			display="flex"
			alignItems="center"
			justifyContent="center"
			width="2rem"
			height="2rem"
			borderRadius="50%"
			position="relative"
			zIndex={1}
			sx={({ palette: { primary, white }, typography: { size } }) => ({
				fontSize: size.sm,
				fontWeight: 700,
				transition: "all 200ms linear",
				border: `2px solid ${white.main}`,
				backgroundColor: active || completed ? white.main : "transparent",
				color: active || completed ? primary.focus : white.main,
				opacity: active || completed ? 1 : 0.6,
				boxShadow: active ? "0 0 0 5px rgba(255, 255, 255, 0.35)" : "none",
				transform: active ? "scale(1.1)" : "none"
			})}
		>
			{completed && !active ? (
				<Icon sx={{ fontSize: "1.1rem !important", fontWeight: "bold" }}>
					check
				</Icon>
			) : (
				icon
			)}
		</MDBox>
	);
}

/**
 * MUI v5 / Material Dashboard 3 version of components/DigiHapi/Wizard.
 * Same API: every step is mounted (inactive ones are hidden) and exposes
 * optional isValidated() / sendState() methods through its ref.
 * Connected steps must be created with connect(..., { forwardRef: true }).
 */
class MDWizard extends Component {
	constructor(props) {
		super(props);
		this.steps = {};
		this.state = {
			currentStep: props.currentStep || 0,
			allStates: {}
		};
	}

	componentDidMount() {
		if (this.state.currentStep) {
			this.navigationStepChange(this.state.currentStep);
		}
	}

	getStep = index => this.steps[this.props.steps[index].stepId];

	// A step without isValidated() is always valid
	isStepValid = index => {
		const step = this.getStep(index);
		return !step || step.isValidated === undefined || step.isValidated();
	};

	collectState = (index, allStates = this.state.allStates) => {
		const step = this.getStep(index);
		if (step && step.sendState !== undefined) {
			return {
				...allStates,
				[this.props.steps[index].stepId]: step.sendState()
			};
		}
		return allStates;
	};

	setCurrentStep = (currentStep, allStates = this.state.allStates) => {
		this.setState(
			{ currentStep, allStates },
			() =>
				this.props.onChangeCurrentStep &&
				this.props.onChangeCurrentStep(currentStep)
		);
	};

	navigationStepChange = key => {
		const { currentStep } = this.state;
		let allStates = this.state.allStates;
		if (key > currentStep) {
			// Every step between the current one and the target must be valid
			for (let i = currentStep; i < key; i++) {
				allStates = this.collectState(i, allStates);
				if (!this.isStepValid(i)) {
					this.setCurrentStep(i, allStates);
					return;
				}
			}
		}
		this.setCurrentStep(key, allStates);
	};

	nextButtonClick = () => {
		const { validate } = this.props;
		const { currentStep } = this.state;
		if (validate === undefined || (validate && this.isStepValid(currentStep))) {
			this.setCurrentStep(currentStep + 1, this.collectState(currentStep));
		}
	};

	previousButtonClick = () => {
		const { currentStep } = this.state;
		if (currentStep > 0) {
			this.setCurrentStep(currentStep - 1, this.collectState(currentStep));
		}
	};

	finishButtonClick = () => {
		const { validate, finishButtonClick } = this.props;
		const { currentStep } = this.state;
		if (
			finishButtonClick !== undefined &&
			(validate === false || (validate && this.isStepValid(currentStep)))
		) {
			const allStates = this.collectState(currentStep);
			this.setState({ allStates }, () => finishButtonClick(allStates));
		}
	};

	render() {
		const {
			title,
			subtitle,
			steps,
			onClose,
			isFetching = false,
			cancelButtonText,
			previousButtonText,
			nextButtonText,
			finishButtonText,
			inline = false,
			isMobile = false
		} = this.props;
		const { currentStep, allStates } = this.state;
		const isLastStep = currentStep === steps.length - 1;

		// On mobile, buttons only show an icon (the label becomes a tooltip)
		const renderButton = ({ label, icon, ...buttonProps }) =>
			isMobile ? (
				<Tooltip title={label}>
					<span>
						<MDButton {...buttonProps} iconOnly aria-label={label}>
							{buttonProps.children || (
								<Icon sx={{ fontSize: "1.25rem !important" }}>{icon}</Icon>
							)}
						</MDButton>
					</span>
				</Tooltip>
			) : (
				<MDButton {...buttonProps}>{buttonProps.children || label}</MDButton>
			);

		const card = (
			<Card>
				<MDBox pt={3} px={isMobile ? 2 : 3} textAlign="center">
					<MDTypography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
						{title}
					</MDTypography>
					{subtitle && (
						<MDTypography variant="button" color="text">
							{subtitle}
						</MDTypography>
					)}
				</MDBox>
				<MDBox mt={2} mb={3} mx={2}>
					<Stepper
						activeStep={currentStep}
						alternativeLabel
						sx={({
							palette: { gradients },
							functions: { linearGradient }
						}) => ({
							background: linearGradient(
								gradients.primary.main,
								gradients.primary.state
							),
							boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.14)",
							// Connector aligned on the middle of the 2rem icons
							"& .MuiStepConnector-alternativeLabel": {
								top: "1rem",
								left: "calc(-50% + 1.5rem)",
								right: "calc(50% + 1.5rem)"
							},
							"& .MuiStepConnector-line": { opacity: 0.4 },
							"& .Mui-completed .MuiStepConnector-line, & .Mui-active .MuiStepConnector-line":
								{ opacity: 1 },
							"& .MuiStepLabel-label": {
								color: "rgba(255, 255, 255, 0.7) !important",
								fontSize: "0.8125rem"
							},
							"& .MuiStepLabel-label.Mui-active": {
								color: "#ffffff !important",
								fontWeight: "700 !important"
							},
							"& .MuiStepLabel-label.Mui-completed": {
								color: "rgba(255, 255, 255, 0.9) !important"
							},
							// On mobile, only the current step shows its label
							...(isMobile && {
								"& .MuiStepLabel-label:not(.Mui-active)": {
									visibility: "hidden"
								},
								"& .MuiStepLabel-label.Mui-active": {
									color: "#ffffff !important",
									fontWeight: "700 !important",
									whiteSpace: "nowrap"
								}
							})
						})}
					>
						{steps.map(({ stepName }, key) => (
							<Step key={stepName}>
								<StepLabel
									StepIconComponent={WizardStepIcon}
									onClick={() => !isFetching && this.navigationStepChange(key)}
									sx={{ cursor: isFetching ? "default" : "pointer" }}
								>
									{stepName}
								</StepLabel>
							</Step>
						))}
					</Stepper>
				</MDBox>
				<MDBox px={isMobile ? 2 : 3} pb={1}>
					{steps.map(({ stepId, stepComponent: StepComponent }, key) => (
						<MDBox
							key={stepId}
							display={currentStep === key ? "block" : "none"}
						>
							<StepComponent
								ref={node => (this.steps[stepId] = node)}
								allStates={allStates}
								disabled={isFetching}
							/>
						</MDBox>
					))}
				</MDBox>
				<MDBox
					p={isMobile ? 2 : 3}
					display="flex"
					justifyContent="space-between"
					alignItems="center"
				>
					<MDBox>
						{currentStep > 0 &&
							renderButton({
								label: previousButtonText,
								icon: "arrow_back",
								variant: "gradient",
								color: "light",
								onClick: this.previousButtonClick,
								disabled: isFetching
							})}
					</MDBox>
					<MDBox display="flex" gap={1}>
						{renderButton({
							label: cancelButtonText,
							icon: "close",
							variant: "outlined",
							color: "secondary",
							onClick: onClose,
							disabled: isFetching
						})}
						{isLastStep
							? renderButton({
									label: finishButtonText,
									icon: "check",
									variant: "gradient",
									color: "primary",
									onClick: this.finishButtonClick,
									disabled: isFetching,
									children: isFetching ? (
										<CircularProgress size={16} color="inherit" />
									) : undefined
							  })
							: renderButton({
									label: nextButtonText,
									icon: "arrow_forward",
									variant: "gradient",
									color: "primary",
									onClick: this.nextButtonClick
							  })}
					</MDBox>
				</MDBox>
			</Card>
		);

		// Creations and editions are displayed in a modal
		return inline ? card : <FormModal>{card}</FormModal>;
	}
}

MDWizard.defaultProps = {
	title: "",
	subtitle: "",
	cancelButtonText: "Annuler",
	previousButtonText: "Précédent",
	nextButtonText: "Suivant",
	finishButtonText: "Terminer"
};

MDWizard.propTypes = {
	steps: PropTypes.arrayOf(
		PropTypes.shape({
			stepName: PropTypes.string.isRequired,
			stepComponent: PropTypes.elementType.isRequired,
			stepId: PropTypes.string.isRequired
		})
	).isRequired,
	title: PropTypes.string,
	subtitle: PropTypes.string,
	currentStep: PropTypes.number,
	onChangeCurrentStep: PropTypes.func,
	onClose: PropTypes.func,
	isFetching: PropTypes.bool,
	cancelButtonText: PropTypes.string,
	previousButtonText: PropTypes.string,
	nextButtonText: PropTypes.string,
	finishButtonText: PropTypes.string,
	finishButtonClick: PropTypes.func,
	validate: PropTypes.bool,
	// Render in the page instead of a modal
	inline: PropTypes.bool,
	// Provided by ResponsiveMDWizard
	isMobile: PropTypes.bool
};

// Gives the class component access to the screen size (buttons and stepper adapt on mobile)
function ResponsiveMDWizard(props) {
	const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));
	return <MDWizard {...props} isMobile={isMobile} />;
}

export default ResponsiveMDWizard;
